import { useState, useEffect } from 'react';
import { attendanceApi } from '../services/api';
import { AttendanceStatus, Punch, PunchType } from '../types/models';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Clock, LogIn, LogOut } from 'lucide-react';

export default function AttendancePage() {
  const [status, setStatus] = useState<AttendanceStatus | null>(null);
  const [punches, setPunches] = useState<Punch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [location, setLocation] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusData, todayPunches] = await Promise.all([
        attendanceApi.getMyStatus() as Promise<AttendanceStatus>,
        attendanceApi.getPunches(
          new Date().toISOString().split('T')[0],
          new Date().toISOString().split('T')[0]
        ) as Promise<Punch[]>,
      ]);
      setStatus(statusData);
      setPunches(todayPunches || []);
    } catch (error) {
      console.error('Error loading attendance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePunch = async (type: 'in' | 'out') => {
    setIsActionLoading(true);
    try {
      if (type === 'in') {
        await attendanceApi.punchIn(location);
      } else {
        await attendanceApi.punchOut(location);
      }
      await loadData();
    } catch (error) {
      console.error('Error punching:', error);
      alert(error instanceof Error ? error.message : 'Error al marcar');
    } finally {
      setIsActionLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Clock className="h-6 w-6" />
          Control de Asistencia
        </h1>
        <p className="text-gray-500">
          {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy")}
        </p>
      </div>

      {/* Clock Card */}
      <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
        <div className="text-center mb-8">
          <div className="text-6xl font-bold text-gray-800 mb-2">
            {format(new Date(), 'HH:mm:ss')}
          </div>
          <div className="text-gray-500">
            {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
          </div>
        </div>

        {/* Status */}
        <div className={`rounded-xl p-6 mb-6 ${
          status?.is_clocked_in
            ? 'bg-green-50 border-2 border-green-500'
            : 'bg-gray-50 border-2 border-gray-300'
        }`}>
          <div className="flex items-center justify-center gap-3">
            {status?.is_clocked_in ? (
              <>
                <LogIn className="h-6 w-6 text-green-600" />
                <span className="text-xl font-semibold text-green-800">
                  EN TURNO - Entrada: {status.last_punch ? format(new Date(status.last_punch), 'HH:mm') : ''}
                </span>
              </>
            ) : (
              <>
                <LogOut className="h-6 w-6 text-gray-600" />
                <span className="text-xl font-semibold text-gray-800">
                  FUERA DE TURNO
                </span>
              </>
            )}
          </div>
        </div>

        {/* Location Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ubicación (opcional)
          </label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Ej: Bay 3, Recepción"
          />
        </div>

        {/* Punch Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handlePunch('in')}
            disabled={isActionLoading || status?.is_clocked_in}
            className={`py-4 rounded-xl font-semibold text-white transition flex flex-col items-center gap-2 ${
              status?.is_clocked_in
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            <LogIn className="h-8 w-8" />
            MARCAR ENTRADA
          </button>

          <button
            onClick={() => handlePunch('out')}
            disabled={isActionLoading || !status?.is_clocked_in}
            className={`py-4 rounded-xl font-semibold text-white transition flex flex-col items-center gap-2 ${
              !status?.is_clocked_in
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            <LogOut className="h-8 w-8" />
            MARCAR SALIDA
          </button>
        </div>
      </div>

      {/* Today's Punches */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Registros de Hoy</h2>
        </div>

        {punches.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay registros hoy
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {punches.map((punch) => (
              <div key={punch.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {punch.type_id === PunchType.IN ? (
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <LogIn className="h-5 w-5 text-green-600" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <LogOut className="h-5 w-5 text-red-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-gray-800">
                      {punch.type_id === PunchType.IN ? 'Entrada' : 'Salida'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(punch.time_stamp), 'HH:mm:ss')}
                      {punch.location && ` - ${punch.location}`}
                    </p>
                  </div>
                </div>
                {punch.note && (
                  <span className="text-sm text-gray-500">{punch.note}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
